import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import html2canvas from 'html2canvas';
import api from '../utils/api';
import './TicketPage.css';

const STATUS_MAP = {
  approved: { label: 'Payment Approved ✓',       cls: 'approved' },
  free:     { label: 'Free Ticket ✓',             cls: 'free'     },
  pending:  { label: 'Awaiting Payment Approval', cls: 'pending'  },
  declined: { label: 'Payment Not Received',      cls: 'declined' }
};

export default function TicketPage() {
  const { ticketNumber } = useParams();
  const navigate          = useNavigate();
  const cardRef           = useRef(null);

  const [ticket,    setTicket]    = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [bannerSrc, setBannerSrc] = useState('');
  const [saving,    setSaving]    = useState(false);
  const [saved,     setSaved]     = useState(false);

  useEffect(() => {
    api.get(`/tickets/${ticketNumber}`)
      .then(r => setTicket(r.data))
      .catch(() => setTicket(null))
      .finally(() => setLoading(false));

    api.get('/gallery?category=promo')
      .then(r => { if (r.data.length > 0) setBannerSrc(`/uploads/gallery/${r.data[0].filename}`); })
      .catch(() => {});
  }, [ticketNumber]);

  const saveTicket = async () => {
    if (!cardRef.current || saving) return;
    setSaving(true);
    try {
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: '#07071a',
        scale: 3,               // 3× for sharp retina quality
        useCORS: true,
        logging: false,
        removeContainer: true
      });

      const filename = `ALS-Ticket-${ticket.ticketNumber}.png`;

      // Mobile: try Web Share API (saves to Photos on iOS/Android)
      if (navigator.share && navigator.canShare) {
        try {
          await new Promise((resolve, reject) => {
            canvas.toBlob(async (blob) => {
              try {
                const file = new File([blob], filename, { type: 'image/png' });
                if (navigator.canShare({ files: [file] })) {
                  await navigator.share({ files: [file], title: 'My ALS Ticket' });
                  setSaved(true);
                  setTimeout(() => setSaved(false), 3000);
                  resolve();
                } else {
                  reject(new Error('canShare false'));
                }
              } catch (err) { reject(err); }
            }, 'image/png', 1.0);
          });
          return;
        } catch {
          // fallthrough to download
        }
      }

      // Desktop / fallback: trigger file download
      const link = document.createElement('a');
      link.download = filename;
      link.href = canvas.toDataURL('image/png', 1.0);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error('Save failed:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="tp"><p className="tp-loading">Loading ticket…</p></div>;
  if (!ticket)  return <div className="tp"><p className="tp-not-found">Ticket not found.</p></div>;

  const status = STATUS_MAP[ticket.paymentStatus] || STATUS_MAP.pending;

  return (
    <div className="tp">
      {/* Card captured by html2canvas */}
      <div className="tp-card" ref={cardRef}>

        {/* Banner */}
        {bannerSrc ? (
          <img src={bannerSrc} alt="ALS Grand Opening" className="tp-banner" crossOrigin="anonymous" />
        ) : (
          <div className="tp-banner-placeholder">
            <span className="tp-banner-placeholder-logo">ALS</span>
          </div>
        )}

        {/* Body */}
        <div className="tp-body">
          <div className="tp-name">{ticket.fullName}</div>

          {ticket.qrCodeData && (
            <div className="tp-qr-box">
              <img src={ticket.qrCodeData} alt="Ticket QR Code" />
              <div className="tp-ticket-num">{ticket.ticketNumber}</div>
            </div>
          )}

          <div className={`tp-status-badge ${status.cls}`}>{status.label}</div>

          <div className="tp-info-grid">
            <div className="tp-info-col">
              <div className="tp-info-icon-row">
                <span>📅</span><span className="tp-info-label">Date</span>
              </div>
              <div className="tp-info-val">Saturday, June 6, 2026</div>
            </div>
            <div className="tp-info-col">
              <div className="tp-info-icon-row">
                <span>🕐</span><span className="tp-info-label">Time</span>
              </div>
              <div className="tp-info-val">1:00 PM – 4:00 PM</div>
            </div>
          </div>

          <div className="tp-location-row">
            <div className="tp-info-icon-row">
              <span>📍</span><span className="tp-info-label">Location</span>
            </div>
            <div className="tp-location-val">
              រាជបណ្ឌិតសភាកម្ពុជា (សាលខេមរៈវិទូ)
            </div>
          </div>
        </div>

        {/* Footer (inside captured area) */}
        <div className="tp-footer-note-wrap">
          <p className="tp-footer-note">Please present this QR code at the entrance</p>
        </div>
      </div>
      {/* ─── End of captured area ─── */}

      {/* Action buttons — outside capture so they don't appear in the saved image */}
      <div className="tp-actions">
        <button
          className={`tp-save-btn ${saving ? 'loading' : ''} ${saved ? 'done' : ''}`}
          onClick={saveTicket}
          disabled={saving}
        >
          {saving ? (
            <><span className="tp-save-spinner" /> Generating…</>
          ) : saved ? (
            <>✓ Saved!</>
          ) : (
            <>📥 Save Ticket to Phone</>
          )}
        </button>

        <button className="tp-back-btn" onClick={() => navigate('/')}>
          ← Back to Event
        </button>
      </div>
    </div>
  );
}
